import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Building2,
  CalendarRange,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  MapPin,
  Plug,
  Wallet,
  Phone,
  Mail,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ChangeEmailSection } from "@/components/dashboard/ChangeEmailSection";
import { DisplayNameSection } from "@/components/dashboard/DisplayNameSection";
import { TwoFactorSection } from "@/components/dashboard/TwoFactorSection";
import { isExternalBookingSystem } from "@/components/dashboard/ExternalSystemNotice";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

interface VenueApp {
  id: string;
  venue_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  region: string;
  postcode: string;
  country: string;
  booking_system: string;
  status: string;
  stripe_connect_status: string | null;
  vp_agreement_signed_at: string | null;
  mindbody_connected_at: string | null;
}

function useVenue() {
  const { profile } = useAuth();
  const [venue, setVenue] = useState<VenueApp | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    if (!profile?.venue_application_id) {
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("venue_applications")
      .select("*")
      .eq("id", profile.venue_application_id)
      .single();
    setVenue((data ?? null) as VenueApp | null);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.venue_application_id]);

  return { venue, loading, reload: load };
}

// -- Overview ----------------------------------------------------------------

export function VenueOverview() {
  const { profile } = useAuth();
  const { venue, loading, reload } = useVenue();
  const [courtCount, setCourtCount] = useState<number | null>(null);
  const [slotCount, setSlotCount] = useState<number | null>(null);
  const [agreementOpen, setAgreementOpen] = useState(false);

  // Query real counts for the checklist so steps auto-tick as the venue
  // completes them, instead of staying perpetually grey.
  useEffect(() => {
    let cancelled = false;
    if (!venue?.id) return;
    (async () => {
      const { count: courts } = await supabase
        .from("courts")
        .select("id", { count: "exact", head: true })
        .eq("venue_application_id", venue.id);
      if (cancelled) return;
      setCourtCount(courts ?? 0);

      // For availability, check that at least one slot exists on any of
      // this venue's courts.
      const { data: courtIds } = await supabase
        .from("courts")
        .select("id")
        .eq("venue_application_id", venue.id);
      const ids = (courtIds ?? []).map((c) => c.id as string);
      if (ids.length === 0) {
        setSlotCount(0);
        return;
      }
      const { count: slots } = await supabase
        .from("availability_slots")
        .select("id", { count: "exact", head: true })
        .in("court_id", ids);
      if (!cancelled) setSlotCount(slots ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [venue?.id]);

  const stripeDone = venue?.stripe_connect_status === "enabled";
  const courtsDone = (courtCount ?? 0) > 0;
  const slotsDone = (slotCount ?? 0) > 0;
  const agreementDone = !!venue?.vp_agreement_signed_at;

  // Tier 1 (external booking system: MINDBODY, Club Automation, etc.)
  // gets a 4-step checklist where courts + availability collapse into a
  // single "Connect [System]" step (auto-handled once direct sync ships
  // for them - they don't action it themselves). Tier 2 (Whistll
  // dashboard) keeps the original 5-step flow.
  const isExternal = isExternalBookingSystem(venue?.booking_system);
  const systemName = (venue?.booking_system ?? "").trim();
  const connectSystemDone = !!venue?.mindbody_connected_at;
  // For external venues, the connect step is "waiting on us" until we
  // ship sync - don't include it in completed-steps until it actually
  // is done, but DO include it in the totalSteps denominator so the
  // pill reads "3 / 4" rather than misleadingly hitting 100%.
  const totalSteps = isExternal ? 4 : 5;
  const completedSteps = isExternal
    ? 1 /* application approved */ +
      (stripeDone ? 1 : 0) +
      (connectSystemDone ? 1 : 0) +
      (agreementDone ? 1 : 0)
    : 1 /* application approved */ +
      (stripeDone ? 1 : 0) +
      (courtsDone ? 1 : 0) +
      (slotsDone ? 1 : 0) +
      (agreementDone ? 1 : 0);
  const allDone = completedSteps === totalSteps;

  return (
    <div>
      <header className="mb-8">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Venue partner
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">
          Welcome{profile?.display_name ? `, ${profile.display_name.split(" ")[0]}` : ""}.
        </h1>
        {loading ? (
          <p className="mt-2 text-muted-foreground">
            <Loader2 className="inline h-4 w-4 animate-spin" /> loading…
          </p>
        ) : venue ? (
          <p className="mt-2 text-muted-foreground">
            Managing <span className="font-semibold text-foreground">{venue.venue_name}</span>{" "}
            · {venue.city}, {venue.country}
          </p>
        ) : null}
      </header>

      {/* Onboarding checklist */}
      <section className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Getting live in the app
          </h2>
          <span
            className={
              "rounded-full px-2.5 py-0.5 text-[10px] font-bold " +
              (allDone
                ? "bg-volt text-primary-foreground"
                : "bg-secondary text-muted-foreground")
            }
          >
            {completedSteps} / {totalSteps}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-4 h-1 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-volt transition-all duration-500"
            style={{
              width: `${(completedSteps / totalSteps) * 100}%`,
            }}
          />
        </div>

        <ul className="flex flex-col gap-2">
          <StepRow
            done={true}
            title="Application approved"
            body="You're in. The rest of this list unlocks your venue in the app."
          />
          <StepRow
            done={stripeDone}
            title="Connect Stripe for payouts"
            cta={
              <Button size="sm" asChild>
                <Link to="../payouts">
                  <Wallet className="h-3.5 w-3.5" />
                  {stripeDone ? "Manage" : "Go to Payouts"}
                </Link>
              </Button>
            }
            body={
              stripeDone
                ? "Stripe is connected - confirmed games will pay out automatically."
                : "Until Stripe is connected, we can't pay you. This is the single biggest blocker to going live."
            }
          />
          {isExternal ? (
            <StepRow
              done={connectSystemDone}
              awaitingPlatform={!connectSystemDone}
              title={
                connectSystemDone
                  ? `${systemName} connected`
                  : `Connect ${systemName}`
              }
              cta={
                <Button size="sm" variant="outline" asChild>
                  <Link to="../integrations">
                    <Plug className="h-3.5 w-3.5" />
                    Integrations
                  </Link>
                </Button>
              }
              body={
                connectSystemDone
                  ? `Direct sync is live for ${venue?.venue_name ?? "your venue"} - courts and availability flow from ${systemName} automatically.`
                  : `Direct sync rolls out ahead of launch. We'll let you know the moment it's ready for ${venue?.venue_name ?? "your venue"} - at that point it's a one-time ~2-min setup (tick which ${systemName} resources are courts + set per-player Whistll price). Until then, nothing for you to do here.`
              }
            />
          ) : (
            <>
              <StepRow
                done={courtsDone}
                title={
                  courtsDone
                    ? `${courtCount} court${courtCount === 1 ? "" : "s"} listed`
                    : "Add your courts"
                }
                cta={
                  <Button size="sm" variant="outline" asChild>
                    <Link to="../courts">
                      <Building2 className="h-3.5 w-3.5" />
                      {courtsDone ? "Manage courts" : "Go to Courts"}
                    </Link>
                  </Button>
                }
                body={
                  courtsDone
                    ? "Add more any time - 5v5, 3v3, half-court all get their own listings."
                    : "Each bookable surface - 5v5, 3v3, half-court - gets its own listing with photos and pricing."
                }
              />
              <StepRow
                done={slotsDone}
                title={
                  slotsDone
                    ? "Availability set"
                    : "Set your first availability slot"
                }
                cta={
                  <Button size="sm" variant="outline" asChild>
                    <Link to="../bookings">
                      <CalendarRange className="h-3.5 w-3.5" />
                      {slotsDone ? "Edit availability" : "Add slots"}
                    </Link>
                  </Button>
                }
                body={
                  slotsDone
                    ? "Players can now discover your courts in Open Runs and Book a Court."
                    : "Players only see your courts in the app when there's availability. Open the Bookings tab and click any empty cell on the weekly grid to add a slot."
                }
              />
            </>
          )}
          <StepRow
            done={agreementDone}
            title={
              agreementDone
                ? "Venue Partner Agreement signed"
                : "Sign the Venue Partner Agreement"
            }
            cta={
              <Button
                size="sm"
                variant={agreementDone ? "outline" : "default"}
                onClick={() => setAgreementOpen(true)}
                disabled={!venue}
              >
                <FileText className="h-3.5 w-3.5" />
                {agreementDone ? "View agreement" : "Review and sign"}
              </Button>
            }
            body={
              agreementDone
                ? `Signed on ${
                    venue?.vp_agreement_signed_at
                      ? new Date(venue.vp_agreement_signed_at).toLocaleDateString()
                      : ""
                  }. Locks in our 20% platform fee on top of your court price (you keep 100% of the court fee), payout schedule, and the rest of the partner terms.`
                : "Final step. Covers the 20% Whistll fee (sits on top of your court price - you keep 100% of the court fee), payout timing, cancellation handling, and the rest of the partner-specific terms."
            }
          />
        </ul>

        {agreementOpen && venue && (
          <SignAgreementModal
            venueId={venue.id}
            alreadySigned={agreementDone}
            signedAt={venue.vp_agreement_signed_at}
            onClose={() => setAgreementOpen(false)}
            onSigned={async () => {
              await reload();
              setAgreementOpen(false);
            }}
          />
        )}

        {allDone && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-volt/40 bg-volt-subtle p-4">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
            <div className="text-sm">
              <p className="font-semibold">You're live.</p>
              <p className="mt-1 text-muted-foreground">
                {isExternal
                  ? `Your ${systemName} courts are discoverable in the Whistll app and availability flows through automatically. Confirmed runs land in the Bookings tab the moment each game hits its player threshold.`
                  : "Your courts are discoverable in the Whistll app. Keep an eye on the Bookings tab - confirmed runs land there the moment each game hits its player threshold."}
              </p>
            </div>
          </div>
        )}

        {/* Tier-1 venues whose checklist isn't fully done yet still need
            to know their courts/availability is hands-off - surface that
            so they don't go hunting for an "Add courts" button. */}
        {isExternal && !allDone && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-border bg-card/60 p-4">
            <Plug className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
            <div className="text-sm">
              <p className="font-semibold">
                {systemName} handles courts + availability
              </p>
              <p className="mt-1 text-muted-foreground">
                You don't need to add courts or set availability in
                Whistll - it all flows from {systemName} once direct
                sync is live. The Courts and Bookings tabs are read-only
                for venues like yours; check the Integrations tab to see
                connection status.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* What to expect */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <h3 className="mb-2 text-sm font-bold">What happens next</h3>
        <p className="text-sm text-muted-foreground">
          Once your courts are live, players in your city start seeing
          you in Open Runs, Book a Court, and Find a Venue. You manage
          availability here (or we pull it from your booking system if
          you're on one we've integrated), and confirmed games appear on
          the Bookings tab. Payouts land in your Stripe account the
          moment each game hits its player threshold.
        </p>
      </section>
    </div>
  );
}

function StepRow({
  done,
  title,
  body,
  cta,
  awaitingPlatform,
}: {
  done: boolean;
  title: string;
  body: string;
  cta?: React.ReactNode;
  /** Render the indicator differently when this step is "waiting on
   *  Whistll" rather than "waiting on the venue" - used for the
   *  Connect [System] step where the integration ships from our side
   *  (clock icon instead of empty circle). */
  awaitingPlatform?: boolean;
}) {
  let indicator: React.ReactNode;
  let indicatorCls: string;
  if (done) {
    indicatorCls = "bg-volt text-primary-foreground";
    indicator = <CheckCircle2 className="h-4 w-4" />;
  } else if (awaitingPlatform) {
    indicatorCls =
      "border border-volt/40 bg-volt-subtle text-volt";
    indicator = <Clock className="h-3.5 w-3.5" />;
  } else {
    indicatorCls =
      "border border-border bg-background text-muted-foreground";
    indicator = "";
  }
  return (
    <li className="flex gap-3 rounded-xl border border-border bg-card p-4">
      <span
        className={
          "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-black " +
          indicatorCls
        }
      >
        {indicator}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="font-semibold">{title}</p>
          {cta}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{body}</p>
      </div>
    </li>
  );
}

// -- Settings ----------------------------------------------------------------

export function VenueSettings() {
  const { venue, loading } = useVenue();

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Venue partner · Settings
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Contact info and address we have on file. Email us at{" "}
          <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
            hello@whistll.com
          </a>{" "}
          to update any of these for now - in-app edits come with the
          next update.
        </p>
      </header>

      <div className="mb-8 space-y-5">
        <DisplayNameSection />
        <TwoFactorSection />
        <ChangeEmailSection />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : venue ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SettingRow label="Venue name">
            <Input value={venue.venue_name} readOnly />
          </SettingRow>
          <SettingRow label="Booking system">
            <Input
              value={
                venue.booking_system === "__none__"
                  ? "None - using Whistll dashboard"
                  : venue.booking_system
              }
              readOnly
            />
          </SettingRow>

          <SettingRow label="Contact name">
            <Input value={venue.contact_name} readOnly />
          </SettingRow>
          <SettingRow label="Contact email">
            <div className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              <Input value={venue.contact_email} readOnly />
            </div>
          </SettingRow>

          <SettingRow label="Contact phone">
            <div className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              <Input value={venue.contact_phone} readOnly />
            </div>
          </SettingRow>
          <SettingRow label="Country">
            <Input value={venue.country} readOnly />
          </SettingRow>

          <SettingRow label="Address" className="sm:col-span-2">
            <div className="flex items-start gap-2">
              <MapPin className="mt-3 h-3.5 w-3.5 text-muted-foreground" />
              <div className="flex-1 space-y-1.5">
                <Input value={venue.address_line1} readOnly />
                {venue.address_line2 && (
                  <Input value={venue.address_line2} readOnly />
                )}
                <Input
                  value={`${venue.city}, ${venue.region}, ${venue.postcode}`}
                  readOnly
                />
              </div>
            </div>
          </SettingRow>
        </div>
      ) : (
        <p className="text-muted-foreground">
          Couldn't load your venue record. Email hello@whistll.com and
          we'll investigate.
        </p>
      )}
    </div>
  );
}

function SettingRow({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

// -- Sign Venue Partner Agreement modal --------------------------------------
//
// Final onboarding step. Shows a scrollable summary of the key terms inline
// (with a link to the full agreement at /venue-terms), a tickbox the venue
// has to check, and a sign button that timestamps vp_agreement_signed_at on
// their venue_applications row. The full text is the source of truth at
// /venue-terms; this dialog distils the parts venues most need to read
// before agreeing so we don't try to fit a 16-section legal doc in a modal.

function SignAgreementModal({
  venueId,
  alreadySigned,
  signedAt,
  onClose,
  onSigned,
}: {
  venueId: string;
  alreadySigned: boolean;
  signedAt: string | null;
  onClose: () => void;
  onSigned: () => Promise<void>;
}) {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function sign() {
    if (!accepted) {
      return toast.error("Tick the box to confirm you agree.");
    }
    setSubmitting(true);
    // Call the SECURITY DEFINER RPC instead of updating the row
    // directly - venue_applications RLS blocks UPDATE for venue
    // admins, and we want to keep that policy locked down for
    // sensitive fields. The RPC only flips vp_agreement_signed_at
    // on the caller's own venue.
    const { error } = await supabase.rpc("sign_venue_partner_agreement");
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Agreement signed. You're all set.");
    await onSigned();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex w-full max-w-2xl flex-col gap-4 rounded-2xl border border-border bg-card p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wider text-volt">
              Final step
            </p>
            <h2 className="text-2xl font-semibold tracking-tight">
              Venue Partner Agreement
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {alreadySigned && signedAt
                ? `Signed on ${new Date(signedAt).toLocaleDateString()}.`
                : "Last updated April 2026."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Key terms summary. Scroll-y so it doesn't overwhelm. */}
        <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-border bg-background p-5 text-sm leading-relaxed">
          <p className="font-semibold text-foreground">
            What you're agreeing to, in plain words:
          </p>
          <ul className="mt-3 space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Whistll's role.</strong>{" "}
              We connect players with your courts. The booking
              relationship is between you and the player; we
              facilitate.
            </li>
            <li>
              <strong className="text-foreground">Fees.</strong> Whistll
              charges players a 20% platform fee{" "}
              <em>on top of</em> your court price. You always receive
              your full court fee.
            </li>
            <li>
              <strong className="text-foreground">Payouts.</strong>{" "}
              Money flows to your Stripe Connect account once a game
              hits its player threshold. Stripe runs payout to your
              bank on its standard 2–7 day schedule.
            </li>
            <li>
              <strong className="text-foreground">Cancellations &amp; clawbacks.</strong>{" "}
              If a confirmed game is refunded after payout, we deduct
              from future payouts (or invoice you within 14 days).
              Force-majeure cancellations don't trigger clawback.
            </li>
            <li>
              <strong className="text-foreground">Venue obligations.</strong>{" "}
              Honour confirmed bookings, keep courts safe and as
              described, hold required permits and public liability
              insurance, no discrimination.
            </li>
            <li>
              <strong className="text-foreground">Non-circumvention.</strong>{" "}
              For 12 months after a player's first booking at your
              venue via Whistll, you don't take them off-platform for
              future bookings.
            </li>
            <li>
              <strong className="text-foreground">Data.</strong> You
              act as an independent data controller for any player
              data we share with you, and you handle it under
              applicable privacy law. No marketing without consent.
            </li>
            <li>
              <strong className="text-foreground">Termination.</strong>{" "}
              Either side can end the partnership; outstanding bookings
              are honoured or refunded.
            </li>
          </ul>
          <p className="mt-5 text-xs">
            This is a summary, not the full legal text. Open the{" "}
            <a
              href="/venue-terms"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-volt underline-offset-2 hover:underline"
            >
              full Venue Partner Agreement
            </a>{" "}
            in a new tab to read every clause before agreeing.
          </p>
        </div>

        <label className="flex items-start gap-3 rounded-lg border border-border bg-secondary/40 p-4 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input accent-volt"
            checked={accepted || alreadySigned}
            disabled={alreadySigned}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span>
            I'm authorised to bind this venue and I agree to the{" "}
            <a
              href="/venue-terms"
              target="_blank"
              rel="noreferrer"
              className="font-semibold underline underline-offset-2 hover:text-foreground"
            >
              Whistll Venue Partner Agreement
            </a>{" "}
            in full.
          </span>
        </label>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {alreadySigned ? "Close" : "Cancel"}
          </Button>
          {!alreadySigned && (
            <Button
              type="button"
              onClick={sign}
              disabled={submitting || !accepted}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Agree and sign
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
